// Dice pools at the table (issue 1524; docs/MRCS/shadowrun-3.md §3.2.3, §3.6, §5.2): a number each combatant carries,
// `{ left, max }` by the pool's key, spent by tests and refreshed — every `round` pool at each Combat Turn's start, every
// `dm` pool at the DM's Refresh. A pool's size is read from its linked character's sheet (the pool's `sheet` purpose, its
// key naming which field) or typed by the DM. A test draws on it for a kind of test its `for` names, at most what its
// `cap` allows for that kind and, on a default, what the route allows (§3.2.2), and never more than is left.
//
// A pool with `uses` (Karma) is not added as dice: its uses (re-rolls, bought dice, a bought success, a disaster
// avoided) are step 15's; here it is carried, shown and refreshed. Pure: no dice, no state of its own.
import { MAX_POOL } from '../dice/pool.js';
/** A pool's size: at most the most dice a throw holds. */
export const POOL_LIMITS = { max: MAX_POOL };
/** Whether a pool is added to a test as dice: one with uses (Karma) is not, nor one spent between turns or on a failed save. */
export const addsDice = (p) => p.uses.length === 0 && !p.for.includes('between_turns') && !p.for.includes('save_override');
/** The kinds of test a pool may be drawn for, as the Test control offers them: its own list. */
export const drawnFor = (p) => [...p.for];
/** Whether a pool may be drawn for this kind of test: its list names it, or names `any`. */
export const drawsFor = (p, kind) => p.for.includes(kind) || p.for.includes('any');
/** The words a kind of test is said with. */
export const POOL_FOR_WORDS = { attack: 'Attack', dodge: 'Dodge', resist: 'Resistance', spell: 'Spellcasting', drain: 'Drain', astral: 'Astral combat', matrix: 'Matrix', vehicle: 'Vehicle', any: 'Any test', between_turns: 'Between turns', save_override: 'A failed save' };
/**
 * The most of a pool's dice a test may take (§3.2.3, §3.2.2), or null for no cap: the pool's own cap for the kind of
 * test (`rating`, the rating in use; `half_rating`, half of it rounded down; none, no cap), then a default's route
 * (`half_rating`, half the rating defaulted to; `half_base`, half the specialization's base skill; `none`, no pool
 * dice at all). What is left is the caller's to apply.
 */
export function poolCap(p, kind, rating, defaulted) {
    const word = p.cap[kind] ?? p.cap.any ?? null;
    let cap = word === 'rating' ? rating : word === 'half_rating' ? Math.floor(rating / 2) : null;
    if (defaulted) {
        // A specialization's base skill is named by the request; without it the route allows nothing.
        const route = defaulted.pool === 'none' ? 0 : defaulted.pool === 'half_rating' ? Math.floor(rating / 2) : Math.floor((defaulted.base ?? 0) / 2);
        cap = cap === null ? route : Math.min(cap, route);
    }
    return cap;
}
/**
 * Whether these pool dice may join a test, by the pool's rule and what is left (§5.2): the spend to stamp, or the
 * refusal in words. `left` null: the combatant has no such pool.
 */
export function poolSpendOf(rule, ask, rating, defaulted, left) {
    if (!rule)
        return "This game system has no such pool.";
    if (!addsDice(rule))
        return `The ${rule.label} is not added as dice.`;
    if (!drawsFor(rule, ask.for))
        return `The ${rule.label} is not drawn on for that kind of test.`;
    if (left === null)
        return `There is no ${rule.label} to spend.`;
    const cap = poolCap(rule, ask.for, rating, defaulted);
    if (cap !== null && ask.dice > cap)
        return cap === 0 ? `No ${rule.label} dice may join this test.` : `At most ${cap} ${rule.label} ${cap === 1 ? 'die' : 'dice'} may join this test.`;
    if (ask.dice > left)
        return `Only ${left} ${rule.label} ${left === 1 ? 'die is' : 'dice are'} left.`;
    return { key: rule.key, label: rule.label, for: ask.for, dice: ask.dice, cap };
}
/** A pool's size from a linked character's sheet numbers: the field of the pool's purpose whose words name its key. */
export function sheetPoolSize(sheet, rule) {
    if (!rule.sheet || !sheet)
        return null;
    // A field named for the pool first ("Astral Combat Pool" also names "combat", issue 1531), then any that names it.
    // Then a key of two words, as a field says them (`legendary_resistance`).
    const n = sheet.find((x) => x.purpose === rule.sheet && x.words[0] === rule.key) ?? sheet.find((x) => x.purpose === rule.sheet && x.words.includes(rule.key)) ?? sheet.find((x) => x.purpose === rule.sheet && x.words.join('_') === rule.key);
    return n && Number.isInteger(n.value) ? Math.max(0, Math.min(POOL_LIMITS.max, n.value)) : null;
}
/** A pool's short word on the tracker's row: its label without a closing "Pool" ("Combat Pool" → "Combat"). */
export const poolWord = (label) => label.replace(/\s+pool$/i, '').trim() || label;
/**
 * A combatant's pools as the tracker's row says them (§5.2): *Combat 6/8 · Karma 3*, in the system's order. A pool the
 * DM refreshes reads as what is left alone; one that refreshes each round, as left of its size, and not at all when its
 * size is 0 (a character with no magic has no Spell Pool to show).
 */
export function poolsLine(pools, rules) {
    if (!pools || !rules)
        return '';
    return rules.flatMap((r) => {
        const p = pools[r.key];
        if (!p || (r.refresh !== 'dm' && p.max === 0))
            return [];
        return [`${poolWord(r.label)} ${r.refresh === 'dm' ? p.left : `${p.left}/${p.max}`}`];
    }).join(' · ');
}
/**
 * Who the DM may ask about between turns (dnd-5e.md §5.5): each combatant with uses of a `between_turns` pool left, once
 * another creature's turn has ended. Not the one whose turn just ended, and not the one starting: its uses are back.
 */
export function betweenTurnsAsks(enc, rules) {
    const n = enc.combatants.length;
    const pools = (rules ?? []).filter((r) => r.for.includes('between_turns'));
    if (!enc.running || !pools.length || (enc.round <= 1 && enc.activeIndex === 0))
        return [];
    const ended = (enc.activeIndex - 1 + n) % n;
    return enc.combatants.flatMap((c, i) => (i === ended || i === enc.activeIndex ? [] : pools.flatMap((r) => {
        const p = c.pools?.[r.key];
        return p && p.left > 0 ? [{ combatant: c.id, name: c.name, key: r.key, label: r.label, left: p.left, max: p.max }] : [];
    })));
}
/** The pool that may turn this token's failed save into a success, and its combatant: one with a use left, or null. */
export function overridePool(combatants, token, rules) {
    const combatant = combatants.find((c) => c.tokens.includes(token));
    const rule = combatant ? rules?.find((r) => r.for.includes('save_override') && (combatant.pools?.[r.key]?.left ?? 0) > 0) : undefined;
    return combatant && rule ? { combatant, key: rule.key, label: rule.label } : null;
}
/** The most buttons a defender's question offers (docs/MRCS/feats.md §6.1 caps an ask at 21). */
export const MAX_POOL_ASK = 21;
/**
 * A defender's question for pool dice (§5.2; the staged attack, step 13, asks it with `AskButtons`): *Dodge: how many
 * Combat Pool dice?*, and each answer from 0 to what may be spent — what is left, within the cap — at most 21 buttons.
 */
export function poolAsk(rule, kind, left, cap) {
    const most = Math.max(0, Math.min(left, cap ?? left, MAX_POOL_ASK - 1));
    return { question: `${POOL_FOR_WORDS[kind]}: how many ${rule.label} dice?`, choices: Array.from({ length: most + 1 }, (_, i) => String(i)) };
}
//# sourceMappingURL=pools.js.map